import React from "react";
import styles from "./PagedCardCollection.module.css";
import { AreaDisplayInfo } from "../../../../Types/AreaDisplayInfo/AreaDisplayInfo";
import { ISelectOption } from "../../../Utils/MultiSelect/MultiSelect";
import CardCollection from "../CardCollection";
import LoadingWrapper from "../../Loading/LoadingWrapper";
import NoItems from "../../NoItems/NoItems";
import { ProductType } from "../../../../Types/ProductType";
import { inDateRange } from "../../../Analyse/CompactFilters/filterUtils";

const roleToCode = (role?: string) => {
  switch ((role || "").trim().toLowerCase()) {
    case "participant": return "P";
    case "reader": return "R";
    case "owner": return "O";
    case "lead": return "L";
    case "moderator": return "M";
    case "approver": return "A";
    default: return null;
  }
}

type AreaFilterSelection = {
  roles?: ISelectOption[];
  partners?: ISelectOption[];
  departments?: ISelectOption[];
  groups?: ISelectOption[];
  templateTypes?: ISelectOption[];
  observees?: ISelectOption[];
  owners?: ISelectOption[];
  /** DayKey ('YYYY-MM-DD') or '' for unset. */
  completedFrom?: string;
  completedTo?: string;
};

const computeDisabledIds = (
  areas: AreaDisplayInfo[],
  sel: AreaFilterSelection
): string[] => {
  const selectedRoleCodes = (sel.roles || []).map(role => roleToCode(role.value)).filter((code): code is "P" | "R" | "O" | "L" | "M" | "A" => code !== null);
  const selectedPartnerNames = new Set((sel.partners || []).map(p => p.label));
  const selectedDepartmentNames = new Set((sel.departments || []).map(d => d.label));
  const selectedGroupNames = new Set((sel.groups || []).map(g => g.label));
  const selectedProductTypes = new Set((sel.templateTypes || []).map(t => parseInt(t.value)));
  const selectedObserveeNames = new Set((sel.observees || []).map(o => o.label));
  const selectedOwnerNames = new Set((sel.owners || []).map(o => o.label));
  const completedFrom = sel.completedFrom || "";
  const completedTo = sel.completedTo || "";

  const matchesAll = (area: AreaDisplayInfo) => {
    // Roles - area must match ALL selected roles
    if (selectedRoleCodes.length > 0) {
      const areaRoles = area.roles || [];
      if (!selectedRoleCodes.every(code => areaRoles.includes(code))) return false;
    }
    // Partners (Organisations) - area must match ALL selected partners
    if (selectedPartnerNames.size > 0) {
      const orgs = area.organisations || [];
      if (!Array.from(selectedPartnerNames).every(partner => orgs.includes(partner))) return false;
    }
    // Departments - area must match ALL selected departments
    if (selectedDepartmentNames.size > 0) {
      const deps = area.departments || [];
      if (!Array.from(selectedDepartmentNames).every(department => deps.includes(department))) return false;
    }
    // Groups - area must match ALL selected groups
    if (selectedGroupNames.size > 0) {
      const groups = area.groups || [];
      if (!Array.from(selectedGroupNames).every(group => groups.includes(group))) return false;
    }
    // Template Types (Product Types) - area must match ALL selected types
    if (selectedProductTypes.size > 0) {
      if (area.productType == null || !Array.from(selectedProductTypes).every(type => type === area.productType)) return false;
    }
    // Observees - area must match ANY selected observee (OR: only one observee per area)
    if (selectedObserveeNames.size > 0) {
      const areaObservee = (area.observee || area.individual || "").trim();
      if (!selectedObserveeNames.has(areaObservee)) return false;
    }
    // Owners - area must match ANY selected owner (OR: only one owner per area)
    if (selectedOwnerNames.size > 0) {
      const areaOwner = (area.ownerName || "").trim();
      if (!selectedOwnerNames.has(areaOwner)) return false;
    }
    // Date Completed - inclusive both ends; an area with no completion date
    // fails as soon as either bound is set.
    if (!inDateRange(area.completedDate, completedFrom, completedTo)) return false;
    return true;
  };

  return areas.filter(a => !matchesAll(a)).map(a => a.id);
}

// New function to reorder areas: matching first, then filtered out
const reorderAreas = (
  areas: AreaDisplayInfo[],
  disabledIds: string[]
): AreaDisplayInfo[] => {
  const matchingAreas = areas.filter(area => !disabledIds.includes(area.id));
  const filteredOutAreas = areas.filter(area => disabledIds.includes(area.id));
  
  return [...matchingAreas, ...filteredOutAreas];
};
  
type PagedCardCollectionProps = {  
  archived: boolean;  
  template: boolean;  
  displayInfos: AreaDisplayInfo[];
  toggleArchiveHandler: (id: string) => void;
  toggleDisabledHandler?: (id: string) => void;
  pageChanged?: (page: number) => void;
  activePage?: number;
  pageCount?: number;
  loading: boolean;
  selectedRoleOption?: ISelectOption[];
  selectedPartners?: ISelectOption[];
  selectedDepartments?: ISelectOption[];
  selectedGroups?: ISelectOption[];
  selectedTemplateTypes?: ISelectOption[];
  selectedObservees?: ISelectOption[];
  selectedOwners?: ISelectOption[];
  /** DayKey ('YYYY-MM-DD') or '' for unset. */
  completedFrom?: string;
  completedTo?: string;
  searchTerm?: string;
  titleChangedHandler?: (id: string, newValue: string) => void;
  showToggleSlider?: boolean;
  productTypeChanged?: (type: ProductType, templateId: string) => void;
  selectable?: boolean;
  selectedIds?: string[];
  onToggleSelect?: (id: string) => void;
  /** Rendered at the top of the list container, directly above the first card. */
  listHeader?: React.ReactNode;
  /** When true, reduces top margin on the card grid (use with listHeader). */
  compactListTop?: boolean;
  hideExportButton?: boolean;
  onCardExport?: (id: string) => void;
};
  
const PagedCardCollection = ({  
  archived,  
  displayInfos = [],   
  template,   
  toggleArchiveHandler,
  toggleDisabledHandler,
  productTypeChanged,
  pageChanged,
  activePage,
  pageCount,
  loading,
  selectedRoleOption,
  selectedPartners = [],
  selectedDepartments = [],
  selectedGroups = [],
  selectedTemplateTypes = [],
  selectedObservees = [],
  selectedOwners = [],
  completedFrom = "",
  completedTo = "",
  searchTerm = "",
  titleChangedHandler,
  showToggleSlider,
  selectable,
  selectedIds,
  onToggleSelect,
  listHeader,
  compactListTop,
  hideExportButton,
  onCardExport,
}: PagedCardCollectionProps) => {

  const toggleArchived = async (id: string) => {
    if (toggleArchiveHandler) {
      await toggleArchiveHandler(id);
    }
  };

  const toggleDisabled = async (id: string) => {
    if (toggleDisabledHandler) {
      await toggleDisabledHandler(id);
    }
  };

  // Compute disabled IDs and reorder areas
  const disabledIds = computeDisabledIds(displayInfos, {
    roles: selectedRoleOption,
    partners: selectedPartners,
    departments: selectedDepartments,
    groups: selectedGroups,
    templateTypes: selectedTemplateTypes,
    observees: selectedObservees,
    owners: selectedOwners,
    completedFrom,
    completedTo,
  });
  const reorderedAreas = reorderAreas(displayInfos, disabledIds);
  
  return (  
    <LoadingWrapper loading={loading}>
      <div className={styles.outerContainer}>  
        {listHeader ? (
          <div className={styles.listHeaderSlot}>{listHeader}</div>
        ) : null}
        {reorderedAreas.length === 0 ? (  
          <NoItems/>
        ) : (  
          <>  
            <CardCollection 
              toggleArchiveHandler={toggleArchived} 
              toggleDisabledHandler={toggleDisabled}
              archived={archived} 
              areas={reorderedAreas.filter(a => !searchTerm || a.title.toLowerCase().includes(searchTerm.toLowerCase()))} 
              template={template} 
              disabledIds={disabledIds} 
              titleChangedHandler={titleChangedHandler}
              showToggleSlider={showToggleSlider}
              productTypeChanged={productTypeChanged}
              selectable={selectable}
              selectedIds={selectedIds}
              onToggleSelect={onToggleSelect}
              compactListTop={compactListTop ?? Boolean(listHeader)}
              hideExportButton={hideExportButton}
              onCardExport={onCardExport}
            />  
            {/* Removed pagination - showing all items in one list */}
          </>  
        )}  
      </div>  
    </LoadingWrapper>
  );  
 
};  
  
export default PagedCardCollection;  